import type {
  SelectorFormConfig,
  SelectorFormMode,
  SelectorFormTrigger,
} from '@/config/shared/form';

import {
  DEFAULT_EMAIL_FIELDS,
  DEFAULT_FIRST_NAME_FIELDS,
  DEFAULT_LAST_NAME_FIELDS,
  DEFAULT_NAME_FIELDS,
  DEFAULT_PHONE_FIELDS,
} from './customer-field-defaults';
import {
  findFieldValue,
  isBrowserElement,
  isHtmlFormElement,
  isHtmlInputElement,
  readAttribute,
  readInputValue,
  trimNonEmpty,
} from './extractor-utils';

export type NormalizedSelectorFormConfig = {
  fields: string[];
  mode: SelectorFormMode;
  minRequired?: number;
  formSelector?: string;
  trigger: SelectorFormTrigger;
  debounceMs?: number;
  fireOnce?: boolean;
};

export type FormConditionEvaluateInput = {
  element?: Element | null;
  form?: HTMLFormElement | null;
  container?: Element | null;
  trigger?: SelectorFormTrigger;
};

export type FormConditionReason =
  | 'matched'
  | 'no_config'
  | 'no_fields'
  | 'no_form'
  | 'trigger_mismatch'
  | 'missing_required_fields'
  | 'min_required_not_met';

export type FormConditionEvaluateResult = {
  matched: boolean;
  reason: FormConditionReason;
  fields: string[];
  filledFields: string[];
  missingFields: string[];
  values: Record<string, string>;
  config: NormalizedSelectorFormConfig | null;
};

const DEFAULT_MODE: SelectorFormMode = 'all';
const DEFAULT_TRIGGER: SelectorFormTrigger = 'auto';

const VALID_TRIGGERS: readonly SelectorFormTrigger[] = [
  'input',
  'change',
  'submit',
  'click',
  'auto',
];

/** Heurísticas por campo conhecido → attributes + field names. */
const FIELD_HEURISTICS: Record<
  string,
  { attrs: string[]; fields: string[]; type?: string; autocomplete?: string }
> = {
  email: {
    attrs: ['data-email'],
    fields: [...DEFAULT_EMAIL_FIELDS],
    type: 'email',
    autocomplete: 'email',
  },
  phone: {
    attrs: ['data-phone', 'data-telefone'],
    fields: [...DEFAULT_PHONE_FIELDS],
    type: 'tel',
    autocomplete: 'tel',
  },
  name: {
    attrs: ['data-name', 'data-nome'],
    fields: [...DEFAULT_NAME_FIELDS],
    autocomplete: 'name',
  },
  firstName: {
    attrs: ['data-first-name', 'data-firstname'],
    fields: [...DEFAULT_FIRST_NAME_FIELDS],
    autocomplete: 'given-name',
  },
  lastName: {
    attrs: ['data-last-name', 'data-lastname'],
    fields: [...DEFAULT_LAST_NAME_FIELDS],
    autocomplete: 'family-name',
  },
  productId: {
    attrs: [
      'data-product-id',
      'data-product',
      'data-sku',
      'data-item-id',
      'data-id',
    ],
    fields: ['productId', 'product_id', 'product', 'sku', 'item_id', 'id'],
  },
  quantity: {
    attrs: ['data-quantity', 'data-qty'],
    fields: ['quantity', 'qty'],
  },
};

/**
 * Avalia `selectorForm` como condição de formulário.
 * Não cria listeners, não dispara eventos e não altera payloads.
 */
export class FormConditionEvaluator {
  private readonly normalized: NormalizedSelectorFormConfig | null;

  private readonly hasConfig: boolean;

  constructor(config?: SelectorFormConfig) {
    this.hasConfig = config !== undefined && config !== null;
    this.normalized = this.hasConfig
      ? normalizeSelectorFormConfig(config)
      : null;
  }

  getConfig(): NormalizedSelectorFormConfig | null {
    if (!this.normalized) return null;
    return {
      ...this.normalized,
      fields: [...this.normalized.fields],
    };
  }

  evaluate(
    input: FormConditionEvaluateInput = {}
  ): FormConditionEvaluateResult {
    if (!this.hasConfig || !this.normalized) {
      return this.result({
        matched: false,
        reason: 'no_config',
        fields: [],
        filledFields: [],
        missingFields: [],
        values: {},
        config: null,
      });
    }

    const config = this.normalized;

    if (config.fields.length === 0) {
      return this.result({
        matched: false,
        reason: 'no_fields',
        fields: [],
        filledFields: [],
        missingFields: [],
        values: {},
        config,
      });
    }

    if (!this.isTriggerCompatible(config.trigger, input.trigger)) {
      return this.result({
        matched: false,
        reason: 'trigger_mismatch',
        fields: [...config.fields],
        filledFields: [],
        missingFields: [...config.fields],
        values: {},
        config,
      });
    }

    const scope = this.resolveScope(input, config);
    if (scope === 'missing_form_selector') {
      return this.result({
        matched: false,
        reason: 'no_form',
        fields: [...config.fields],
        filledFields: [],
        missingFields: [...config.fields],
        values: {},
        config,
      });
    }

    if (!scope) {
      return this.result({
        matched: false,
        reason: 'no_form',
        fields: [...config.fields],
        filledFields: [],
        missingFields: [...config.fields],
        values: {},
        config,
      });
    }

    const values: Record<string, string> = {};
    const filledFields: string[] = [];
    const missingFields: string[] = [];

    for (const field of config.fields) {
      const value = resolveFieldValue(scope, field);
      if (value) {
        values[field] = value;
        filledFields.push(field);
      } else {
        missingFields.push(field);
      }
    }

    const matched = this.matchesRequirement(
      config,
      filledFields.length,
      config.fields.length
    );

    let reason: FormConditionReason = 'matched';
    if (!matched) {
      reason =
        config.minRequired != null
          ? 'min_required_not_met'
          : 'missing_required_fields';
    }

    return this.result({
      matched,
      reason,
      fields: [...config.fields],
      filledFields,
      missingFields,
      values,
      config,
    });
  }

  private matchesRequirement(
    config: NormalizedSelectorFormConfig,
    filledCount: number,
    total: number
  ): boolean {
    if (config.minRequired != null) {
      const required = Math.max(0, config.minRequired);
      return filledCount >= required;
    }

    if (config.mode === 'any') {
      return filledCount >= 1;
    }

    // mode: all
    return filledCount === total && total > 0;
  }

  private isTriggerCompatible(
    configured: SelectorFormTrigger,
    received?: SelectorFormTrigger
  ): boolean {
    if (configured === 'auto') return true;
    if (received == null) return false;
    return received === configured;
  }

  /**
   * Ordem de resolução do escopo:
   * 1. formSelector (se configurado) — exige elemento dentro do form
   * 2. form explícito no input
   * 3. element.form / closest('form')
   * 4. container explícito
   * 5. próprio element
   */
  private resolveScope(
    input: FormConditionEvaluateInput,
    config: NormalizedSelectorFormConfig
  ): Element | null | 'missing_form_selector' {
    const element = isBrowserElement(input.element) ? input.element : null;

    if (config.formSelector) {
      const found = safeQuerySelector(config.formSelector);
      if (!found) return 'missing_form_selector';

      if (isHtmlFormElement(input.form)) {
        if (found === input.form || found.contains(input.form)) return found;
        return null;
      }

      if (element) {
        if (found === element || found.contains(element)) return found;
        return null;
      }

      return found;
    }

    if (isHtmlFormElement(input.form)) {
      return input.form;
    }

    if (element) {
      const ownerForm = getOwnerForm(element);
      if (ownerForm) return ownerForm;

      const closestForm = element.closest('form');
      if (isHtmlFormElement(closestForm)) return closestForm;
    }

    if (isBrowserElement(input.container)) {
      return input.container;
    }

    if (element) return element;

    return null;
  }

  private result(
    partial: FormConditionEvaluateResult
  ): FormConditionEvaluateResult {
    return {
      ...partial,
      config: partial.config
        ? {
            ...partial.config,
            fields: [...partial.config.fields],
          }
        : null,
      fields: [...partial.fields],
      filledFields: [...partial.filledFields],
      missingFields: [...partial.missingFields],
      values: { ...partial.values },
    };
  }
}

export function normalizeSelectorFormConfig(
  config: SelectorFormConfig | undefined | null
): NormalizedSelectorFormConfig | null {
  if (config == null) return null;

  if (Array.isArray(config)) {
    const fields = sanitizeFields(config);
    return {
      fields,
      mode: DEFAULT_MODE,
      trigger: DEFAULT_TRIGGER,
    };
  }

  if (typeof config !== 'object') return null;

  const fields = sanitizeFields(config.fields ?? []);
  const mode: SelectorFormMode =
    config.mode === 'any' || config.mode === 'all' ? config.mode : DEFAULT_MODE;
  const trigger: SelectorFormTrigger = VALID_TRIGGERS.includes(
    config.trigger as SelectorFormTrigger
  )
    ? (config.trigger as SelectorFormTrigger)
    : DEFAULT_TRIGGER;

  const normalized: NormalizedSelectorFormConfig = {
    fields,
    mode,
    trigger,
  };

  if (
    typeof config.minRequired === 'number' &&
    Number.isFinite(config.minRequired)
  ) {
    normalized.minRequired = config.minRequired;
  }

  const formSelector = trimNonEmpty(config.formSelector);
  if (formSelector) normalized.formSelector = formSelector;

  if (
    typeof config.debounceMs === 'number' &&
    Number.isFinite(config.debounceMs)
  ) {
    normalized.debounceMs = config.debounceMs;
  }

  if (typeof config.fireOnce === 'boolean') {
    normalized.fireOnce = config.fireOnce;
  }

  return normalized;
}

function sanitizeFields(fields: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const field of fields) {
    const trimmed = typeof field === 'string' ? field.trim() : '';
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

function getOwnerForm(element: Element): HTMLFormElement | null {
  if (
    isHtmlInputElement(element) ||
    element instanceof HTMLButtonElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  ) {
    const form = element.form;
    return isHtmlFormElement(form) ? form : null;
  }
  return null;
}

function safeQuerySelector(selector: string): Element | null {
  if (typeof document === 'undefined') return null;
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function resolveFieldValue(scope: Element, field: string): string | undefined {
  const key = field.trim();
  if (!key) return undefined;

  const heuristic = FIELD_HEURISTICS[key];

  if (heuristic?.type) {
    const typed = readTypedInput(scope, heuristic.type);
    if (typed) return typed;
  }

  if (heuristic?.autocomplete) {
    const auto = readAutocomplete(scope, heuristic.autocomplete);
    if (auto) return auto;
  }

  if (heuristic) {
    const fromAttr = readAttribute(scope, heuristic.attrs);
    if (fromAttr) return fromAttr;

    const fromFields = findFieldValue(scope, heuristic.fields);
    if (fromFields) return fromFields;
  }

  // Campo customizado / fallback genérico
  const kebab = toKebabCase(key);
  const fromCustomAttr = readAttribute(scope, [`data-${kebab}`, `data-${key}`]);
  if (fromCustomAttr) return fromCustomAttr;

  return findFieldValue(scope, [key, kebab]);
}

function readTypedInput(root: Element, type: string): string | undefined {
  const selector = `input[type="${type}"]`;
  try {
    const input = root.matches(selector) ? root : root.querySelector(selector);
    return input ? readInputValue(input) : undefined;
  } catch {
    return undefined;
  }
}

function readAutocomplete(
  root: Element,
  autocomplete: string
): string | undefined {
  const selector = `input[autocomplete="${autocomplete}"]`;
  try {
    const input = root.matches(selector) ? root : root.querySelector(selector);
    return input ? readInputValue(input) : undefined;
  } catch {
    return undefined;
  }
}

function toKebabCase(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase();
}
